<script lang="ts">
import { defineComponent, defineAsyncComponent, computed, ref, watch, onMounted } from "vue";
import { useRoute } from "vue-router"; 
import NavbarInd from './pages/components/Generales/NavbarInd.vue';

export default defineComponent({
    name: "App",
    components: {
        // Carga asíncrona de componentes pesados
        FooterInd: defineAsyncComponent(() => import('./pages/components/Generales/FooterInd.vue')),
        NavbarInd: NavbarInd,
    },
    setup() {
        const route = useRoute();
        const isPageLoading = ref(false);

        // Lógica de WhatsApp por Campus
        const datosPorCampus: Record<string, { numero: string; mensaje: string }> = {
            cancun: { numero: '5219987891204', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Cancún' },
            guadalajara: { numero: '5213324296455', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Guadalajara' },
            monterrey: { numero: '5218110365349', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Monterrey' },
            merida: { numero: '5219993564461', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Mérida' },
            virtual: { numero: '5215621798305', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Virtual' },
            "presa-madin": { numero: '5215574339700', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Presa Madín' },
            "del-valle": { numero: '5215545452967', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Del Valle' },
            "santa-fe": { numero: '5215548793217', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Santa Fe' },
            "los-reyes": { numero: '5215582609970', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Los Reyes' },
            "riviera-maya": { numero: '5219841777617', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Riviera Maya' },
            tijuana: { numero: '5216647687631', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Tijuana' },
            queretaro: { numero: '5214421443025', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Querétaro' },
            chihuahua: { numero: '5216142814826', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Chihuahua' },
            angelopolis: { numero: '5212221201633', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas Campus Angelópolis' },
            default: { numero: '5215611422526', mensaje: '¡Hola! Quiero más información sobre Universidad Humanitas' }
        };

        const campus = computed(() => {
            const match = route.path.match(/^\/campus\/([^\/\?]+)/);
            return match ? match[1] : 'default';
        });

        const whatsappLink = computed(() => {
            const { numero, mensaje } = datosPorCampus[campus.value] || datosPorCampus.default;
            return `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(mensaje)}`;
        });

        const isClient = ref(false);

        onMounted(() => {
            isClient.value = true;
        });

        // --- CONTROL DE CARGA Y SCROLL ---
        watch(() => route.path, () => {
            isPageLoading.value = true;
            document.body.style.overflow = 'hidden'; // Bloquea scroll para evitar saltos visuales

            // Pausa controlada para que el DOM se asiente
            setTimeout(() => {
                isPageLoading.value = false;
                document.body.style.overflow = ''; // Libera scroll
                window.scrollTo(0, 0); // Forzar inicio de página
            }, 600); // 600ms es el tiempo ideal para procesos asíncronos y transición
        });

        return {
            route,
            whatsappLink,
            isPageLoading,
            isClient
        };
    },
    methods: {
        showBanner() {
            const _hsp = (window as any)._hsp || [];
            _hsp.push(["showBanner"]);
        },
    },
});
</script>

<template>
    <div v-cloak class="flex flex-col min-h-screen w-full bg-white relative">
        
        <transition name="fade-overlay">
            <div v-if="isPageLoading" class="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center">
                <img src="/pagesImages/logo-h-white.png" class="w-24 h-auto animate-pulse mb-4" alt="Cargando..." />
                <div class="w-48 h-1 bg-gray-100 overflow-hidden">
                    <div class="progress-bar h-full bg-black"></div>
                </div>
            </div>
        </transition>

        <NavbarInd />

        <main class="flex-1 w-full min-h-[80vh]">
            <router-view v-slot="{ Component, route }">
                
                <transition v-if="isClient" name="fade-page" mode="out-in">
                    <keep-alive>
                        <component :is="Component" :key="route.path" />
                    </keep-alive>
                </transition>

                <component v-else :is="Component" :key="route.path" />

            </router-view>
        </main>

        <div class="w-full min-h-[300px] bg-white">
            <FooterInd />
        </div>

        <div class="fixed bottom-4 right-4 z-50 group">
            <a :href="whatsappLink" target="_blank" class="block transition-transform hover:scale-110">
                <img src="/pagesImages/wha-icon.png" width="56" height="55" loading="eager" class="w-[56px] h-[55px] drop-shadow-lg" alt="WhatsApp" />
            </a>
        </div>

        <div class="fixed bottom-4 left-4 z-50 flex items-center justify-center cursor-pointer transition-opacity hover:opacity-80" @click="showBanner">
            <img src="/pagesImages/logo-h-white.png" width="45" height="49" loading="eager" class="w-[45px] h-[49px] drop-shadow-md" alt="Cookies" />
        </div>
    </div>
</template>

<style scoped>
/* Transición de la Página */
.fade-page-enter-active,
.fade-page-leave-active {
    transition: opacity 0.4s ease;
}
.fade-page-enter-from,
.fade-page-leave-to {
    opacity: 0;
}

/* Transición del Overlay de Carga */
.fade-overlay-leave-active {
    transition: opacity 0.5s ease;
}
.fade-overlay-leave-to {
    opacity: 0;
}

/* Animación de barra de carga */
.progress-bar {
    width: 100%;
    animation: loading 1.5s infinite ease-in-out;
    transform-origin: left;
}

@keyframes loading {
    0% { transform: scaleX(0); }
    50% { transform: scaleX(1); }
    100% { transform: scaleX(0); transform-origin: right; }
}

/* Evitar scroll horizontal accidental */
:deep(body) {
    overflow-x: hidden;
    width: 100%;
}
</style>